import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Grid, Paper, Typography, Stack, Button, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Breadcrumbs, Dialog, DialogTitle,
    DialogContent, DialogActions, MenuItem, IconButton
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Search as SearchIcon } from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import axios from 'axios';
import { format } from 'date-fns';
import { getApiUrl } from '../config/api';

const CATEGORIES = ['ACADEMIC', 'SPORTS', 'HOLIDAY', 'GENERAL'];

const categoryColors: any = {
    ACADEMIC: { bg: '#EBF8FF', text: '#3182CE' },
    SPORTS: { bg: '#FFF5F5', text: '#E53E3E' },
    HOLIDAY: { bg: '#E6FFFA', text: '#38A169' },
    GENERAL: { bg: '#F7FAFC', text: '#4A5568' }
};

const EventsPage = () => {
    const [events, setEvents] = useState<any[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingEvent, setEditingEvent] = useState<any>(null);
    const [userRole, setUserRole] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        startDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        endDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        location: '',
        category: 'GENERAL',
        allDay: false
    });

    const canManageEvents = ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'PRINCIPAL'].includes(userRole);

    const fetchEvents = useCallback(async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const role = userInfo ? (JSON.parse(userInfo).user?.role || JSON.parse(userInfo).role) : '';
            setUserRole(role);

            const res = await axios.get(getApiUrl('events'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEvents(res.data);
        } catch (error) {
            console.error('Fetch events error:', error);
        }
    }, []);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    useEffect(() => {
        if (searchParams.get('action') === 'create' && canManageEvents) {
            handleOpenCreate();
            // Clear the param after opening so refreshing doesn't re-open it indefinitely
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('action');
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, canManageEvents, setSearchParams]);

    const handleOpenCreate = () => {
        setEditingEvent(null);
        setFormData({
            title: '',
            description: '',
            startDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
            endDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
            location: '',
            category: 'GENERAL',
            allDay: false
        });
        setOpenDialog(true);
    };

    const handleOpenEdit = (event: any) => {
        setEditingEvent(event);
        setFormData({
            title: event.title,
            description: event.description || '',
            startDate: format(new Date(event.startDate), "yyyy-MM-dd'T'HH:mm"),
            endDate: format(new Date(event.endDate), "yyyy-MM-dd'T'HH:mm"),
            location: event.location || '',
            category: event.category,
            allDay: event.allDay
        });
        setOpenDialog(true);
    };

    const handleSubmit = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            if (editingEvent) {
                await axios.put(getApiUrl(`events/${editingEvent.id}`), formData, config);
            } else {
                await axios.post(getApiUrl('events'), formData, config);
            }

            setOpenDialog(false);
            fetchEvents();
        } catch (error) {
            console.error('Submit event error:', error);
            alert('Failed to save event');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this event?')) return;
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            await axios.delete(getApiUrl(`events/${id}`), {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchEvents();
        } catch (error) {
            console.error('Delete event error:', error);
            alert('Failed to delete event');
        }
    };

    const filteredEvents = events.filter(e =>
        e.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (e.location && e.location.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <Box sx={{ p: 4, bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography variant="h4" fontWeight={800} color="#263B7C">School Calendar</Typography>
                    <Typography variant="body2" color="text.secondary">Manage and view official school schedule and events</Typography>
                </Box>
                <Breadcrumbs separator="/" aria-label="breadcrumb">
                    <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>Dashboard</Typography>
                    <Typography color="#F9B43F" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Calendar</Typography>
                </Breadcrumbs>
            </Stack>

            <Stack direction="row" spacing={2} mb={3} justifyContent="space-between" alignItems="center">
                <TextField
                    placeholder="Search events, categories, location..."
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
                        sx: { bgcolor: 'white', borderRadius: '12px', width: 350 }
                    }}
                />
                {canManageEvents && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                        sx={{ bgcolor: '#263B7C', borderRadius: '12px', px: 3, fontWeight: 700 }}
                    >
                        NEW EVENT
                    </Button>
                )}
            </Stack>

            <Paper elevation={0} sx={{ borderRadius: '24px', border: '1px solid #edf2f7', overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>DATE & TIME</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>EVENT TITLE</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>CATEGORY</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>LOCATION</TableCell>
                                {canManageEvents && <TableCell align="right" sx={{ fontWeight: 700 }}>ACTIONS</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredEvents.map((event) => (
                                <TableRow key={event.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={700}>
                                            {format(new Date(event.startDate), 'MMM dd, yyyy')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {format(new Date(event.startDate), 'hh:mm a')} - {format(new Date(event.endDate), 'hh:mm a')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={700} color="#263B7C">{event.title}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {event.description}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={event.category}
                                            size="small"
                                            sx={{
                                                bgcolor: categoryColors[event.category]?.bg,
                                                color: categoryColors[event.category]?.text,
                                                fontWeight: 800,
                                                fontSize: '0.65rem'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="body2">{event.location || 'School Campus'}</Typography>
                                        </Stack>
                                    </TableCell>
                                    {canManageEvents && (
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <IconButton size="small" onClick={() => handleOpenEdit(event)} sx={{ color: '#263B7C' }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleDelete(event.id)} sx={{ color: '#E53E3E' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {filteredEvents.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={canManageEvents ? 5 : 4} align="center" sx={{ py: 10 }}>
                                        <CalendarMonthIcon sx={{ fontSize: 64, color: '#CBD5E0', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary">No events found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Create/Edit Event Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
                <DialogTitle sx={{ fontWeight: 800, color: '#263B7C' }}>
                    {editingEvent ? 'Edit School Event' : 'Create New Event'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            label="Event Title"
                            fullWidth
                            required
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <TextField
                                    label="Start Date & Time"
                                    type="datetime-local"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={formData.startDate}
                                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <TextField
                                    label="End Date & Time"
                                    type="datetime-local"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={formData.endDate}
                                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                        <TextField
                            label="Location"
                            fullWidth
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        />
                        <TextField
                            select
                            label="Category"
                            fullWidth
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        >
                            {CATEGORIES.map((cat) => (
                                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 4, pt: 0 }}>
                    <Button onClick={() => setOpenDialog(false)} sx={{ fontWeight: 700, borderRadius: '12px' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{ bgcolor: '#263B7C', fontWeight: 700, borderRadius: '12px', px: 4 }}
                    >
                        {editingEvent ? 'Update Event' : 'Schedule Event'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EventsPage;
